import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { RotateCw, Sparkles } from "lucide-react";
import { COLOR_HEX } from "@/lib/pricing";

const GLASS = "url(#glassGrad)";

const GlassDefs = () => (
  <defs>
    <linearGradient id="glassGrad" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stopColor="#cfe3ee" />
      <stop offset="45%" stopColor="#e9f2f7" />
      <stop offset="55%" stopColor="#dcebf3" />
      <stop offset="100%" stopColor="#b9d2e2" />
    </linearGradient>
    <linearGradient id="roofGrad" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="#dfe9ef" />
      <stop offset="100%" stopColor="#b5c9d6" />
    </linearGradient>
    <linearGradient id="steelGrad" x1="0" y1="0" x2="1" y2="0">
      <stop offset="0%" stopColor="#eef1f4" />
      <stop offset="45%" stopColor="#b8bec5" />
      <stop offset="55%" stopColor="#dfe3e8" />
      <stop offset="100%" stopColor="#a4abb3" />
    </linearGradient>
    <pattern id="mesh" width="4" height="4" patternUnits="userSpaceOnUse">
      <path d="M0 0L4 4M4 0L0 4" stroke="#7d8891" strokeWidth="0.5" opacity="0.7" />
    </pattern>
  </defs>
);

// Detail-Wrapper: animiert jede Änderung sichtbar (Aufpoppen)
const Detail = ({ k, children }) => (
  <motion.g
    key={k || "none"}
    initial={{ opacity: 0, scale: 0.5 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ type: "spring", stiffness: 260, damping: 18 }}
    style={{ transformBox: "fill-box", transformOrigin: "center" }}
  >
    {children}
  </motion.g>
);

// ---------- Fenster-Griffe ----------
const WindowHandle = ({ variant, flip }) => {
  const x = flip ? 118 : 272;
  const y = 140;
  switch (variant) {
    case "Abschließbarer Griff":
      return (
        <g>
          <rect x={flip ? x - 10 : x} y={y} width="10" height="26" rx="4" fill="#d7dade" stroke="#9aa0a6" strokeWidth="1" />
          <circle cx={flip ? x - 5 : x + 5} cy={y + 36} r="4.5" fill="#c4c9cf" stroke="#8b959d" strokeWidth="1" />
          <circle cx={flip ? x - 5 : x + 5} cy={y + 36} r="1.4" fill="#5a6066" />
        </g>
      );
    case "Edelstahl-Griff":
      return (
        <g>
          <rect x={flip ? x - 7 : x} y={y} width="7" height="30" rx="3.5" fill="url(#steelGrad)" stroke="#8b959d" strokeWidth="0.8" />
          <line x1={flip ? x - 5 : x + 2} y1={y + 3} x2={flip ? x - 5 : x + 2} y2={y + 27} stroke="#ffffff" strokeWidth="1" opacity="0.7" />
        </g>
      );
    case "Design-Griff":
      return (
        <g>
          <rect x={flip ? x - 14 : x + 2} y={y - 4} width="12" height="12" rx="2" fill="#e8eaed" stroke="#9aa0a6" strokeWidth="1" />
          <polygon
            points={flip ? `${x - 2},${y} ${x - 24},${y + 12} ${x - 20},${y + 18} ${x},${y + 6}` : `${x + 2},${y} ${x + 24},${y + 12} ${x + 20},${y + 18} ${x},${y + 6}`}
            fill="#dfe2e6" stroke="#9aa0a6" strokeWidth="1"
          />
        </g>
      );
    default: // Standard-Griff
      return <rect x={flip ? x - 10 : x} y={y} width="10" height="26" rx="4" fill="#d7dade" stroke="#9aa0a6" strokeWidth="1" />;
  }
};

// Pilzkopf-/RC2-Verriegelung am Flügelrand
const WindowSecurity = ({ variant, flip }) => {
  if (variant !== "Pilzkopf-Verriegelung" && variant !== "RC2 einbruchhemmend") return null;
  const x = flip ? 116 : 284;
  const pins = variant === "RC2 einbruchhemmend" ? [70, 120, 185, 240] : [90, 155, 220];
  return (
    <g>
      {pins.map((py) => (
        <g key={py}>
          <circle cx={flip ? x - 4 : x + 4} cy={py} r="3" fill="#c9ced4" stroke="#7d8891" strokeWidth="0.8" />
          <rect x={flip ? x - 12 : x + 8} y={py - 4} width="4" height="8" rx="1" fill="#9aa0a6" />
        </g>
      ))}
    </g>
  );
};

const Frame = ({ x, y, w, h, c, frame = 10, children }) => (
  <g>
    <rect x={x} y={y} width={w} height={h} rx="3" fill={c} />
    <rect x={x + frame} y={y + frame} width={w - 2 * frame} height={h - 2 * frame} fill={GLASS} />
    {children}
  </g>
);

const WindowSVG = ({ c, opts = {}, flip }) => {
  const { oeffnung, sprossen, griff, sicherheit, dichtung } = opts;
  const gasket = dichtung === "Premium schwarz" ? "#22262a" : "#b9bfc4";
  return (
    <Frame x={90} y={30} w={220} h={240} c={c}>
      <rect x={104} y={44} width={192} height={212} fill="none" stroke={c} strokeWidth="6" />
      {/* Dichtung sichtbar als feine Linie am Flügel */}
      <Detail k={dichtung}>
        <rect x={111} y={51} width={178} height={198} fill="none" stroke={gasket} strokeWidth="2" />
      </Detail>
      {oeffnung === "Zweiflügelig" && <line x1={200} y1={44} x2={200} y2={256} stroke={c} strokeWidth="6" />}
      {sprossen === "Glasteilende Sprossen" && (
        <g stroke={c} strokeWidth={oeffnung === "Zweiflügelig" ? 0 : 4}>
          <line x1={104} y1={114} x2={296} y2={114} />
          <line x1={104} y1={186} x2={296} y2={186} />
          {oeffnung !== "Zweiflügelig" && <line x1={200} y1={44} x2={200} y2={256} />}
        </g>
      )}
      {sprossen === "Aufsatzsprossen" && (
        <g stroke={c} strokeWidth="6">
          <line x1={104} y1={114} x2={296} y2={114} />
          <line x1={104} y1={186} x2={296} y2={186} />
          <line x1={200} y1={44} x2={200} y2={256} />
        </g>
      )}
      {(oeffnung === "Dreh-Kipp" || !oeffnung) && (
        <g stroke="#8b959d" strokeWidth="1.6" strokeDasharray="5 4">
          <line x1={flip ? 108 : 292} y1={48} x2={200} y2={252} />
          <line x1={200} y1={48} x2={flip ? 108 : 292} y2={252} opacity="0.45" />
        </g>
      )}
      {oeffnung !== "Fest verglast" && (
        <Detail k={griff}>
          <WindowHandle variant={griff} flip={flip} />
        </Detail>
      )}
      <Detail k={sicherheit}>
        <WindowSecurity variant={sicherheit} flip={flip} />
      </Detail>
    </Frame>
  );
};

// ---------- Haustür-Griffe ----------
const DoorHandle = ({ variant, right }) => {
  const baseX = right ? 216 : 156;
  switch (variant) {
    case "Stoßgriff":
      return (
        <g>
          <rect x={baseX - 4} y={86} width="8" height="10" rx="3" fill="url(#steelGrad)" stroke="#8b959d" strokeWidth="0.8" />
          <rect x={baseX - 4} y={186} width="8" height="10" rx="3" fill="url(#steelGrad)" stroke="#8b959d" strokeWidth="0.8" />
          <path
            d={right ? `M ${baseX} 96 Q ${baseX + 22} 141 ${baseX} 186` : `M ${baseX + 4} 96 Q ${baseX - 18} 141 ${baseX + 4} 186`}
            fill="none" stroke="url(#steelGrad)" strokeWidth="9" strokeLinecap="round"
          />
        </g>
      );
    case "Klassischer Drücker":
      return (
        <g>
          <circle cx={baseX + 2} cy={140} r="7" fill="#e8eaed" stroke="#9aa0a6" strokeWidth="1" />
          <rect x={right ? baseX - 30 : baseX + 2} y={136} width="32" height="8" rx="4" fill="#d7dade" stroke="#9aa0a6" strokeWidth="1" />
        </g>
      );
    default: // Griffstange Edelstahl
      return (
        <g>
          <rect x={baseX} y={80} width="6" height="110" rx="3" fill="url(#steelGrad)" stroke="#8b959d" strokeWidth="0.8" />
          <rect x={baseX - 3} y={80} width="12" height="6" rx="3" fill="#c9ced4" stroke="#8b959d" strokeWidth="0.6" />
          <rect x={baseX - 3} y={184} width="12" height="6" rx="3" fill="#c9ced4" stroke="#8b959d" strokeWidth="0.6" />
        </g>
      );
  }
};

// ---------- Haustür-Schlösser ----------
const DoorLock = ({ variant, right }) => {
  if (!variant) return null;
  const x = right ? 226 : 148;
  return (
    <g>
      {variant === "Fingerscanner" ? (
        <g>
          <rect x={right ? x - 2 : x - 8} y={150} width="12" height="26" rx="4" fill="#22262a" stroke="#4a5054" strokeWidth="1" />
          <circle cx={right ? x + 4 : x - 2} cy={158} r="3.5" fill="#e2001a" opacity="0.9" />
          <line x1={right ? x : x - 6} y1={166} x2={right ? x + 8 : x + 2} y2={166} stroke="#6b7280" strokeWidth="1.5" />
        </g>
      ) : (
        <g>
          <rect x={right ? x : x - 4} y={146} width="6" height="20" rx="2" fill="#9aa0a6" />
          <circle cx={right ? x + 3 : x - 1} cy={156} r="2.4" fill="#5a6066" />
          {variant === "Mehrfachverriegelung" && (
            <g fill="#9aa0a6">
              <rect x={right ? x + 1 : x - 5} y={96} width="4" height="12" rx="1.5" />
              <rect x={right ? x + 1 : x - 5} y={196} width="4" height="12" rx="1.5" />
              <rect x={right ? x + 1 : x - 5} y={236} width="4" height="10" rx="1.5" />
            </g>
          )}
        </g>
      )}
    </g>
  );
};

const DoorSVG = ({ c, opts = {}, flip }) => {
  const { seitenteil, griff, schloss, oeffnungsrichtung, sicherheit, dichtung } = opts;
  const handleRight = flip !== (oeffnungsrichtung === "DIN rechts");
  const gasket = dichtung === "Premium-Dreifachdichtung" ? "#22262a" : "#b9bfc4";
  return (
    <g>
      {seitenteil !== "Ohne" && seitenteil && (
        <>
          {(seitenteil === "Links" || seitenteil === "Beidseitig") && (
            <Frame x={flip ? 250 : 60} y={40} w={70} h={230} c={c} frame={6} />
          )}
          {(seitenteil === "Rechts" || seitenteil === "Beidseitig") && (
            <Frame x={flip ? 60 : 250} y={40} w={70} h={230} c={c} frame={6} />
          )}
        </>
      )}
      <rect x={140} y={40} width={100} height={230} rx="3" fill={c} />
      <rect x={152} y={52} width={76} height={206} rx="2" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="2" />
      <Detail k={dichtung}>
        <rect x={146} y={46} width={88} height={218} rx="2" fill="none" stroke={gasket} strokeWidth="1.6" />
      </Detail>
      <Detail k={griff}>
        <DoorHandle variant={griff || "Griffstange Edelstahl"} right={handleRight} />
      </Detail>
      <Detail k={schloss}>
        <DoorLock variant={schloss} right={handleRight} />
      </Detail>
      {sicherheit === "RC2 einbruchhemmend" && (
        <Detail k="rc2">
          <g fill="#c9ced4" stroke="#7d8891" strokeWidth="0.7">
            {[70, 250].map((py) => (
              <rect key={py} x={handleRight ? 136 : 250} y={py} width="6" height="16" rx="2" />
            ))}
          </g>
        </Detail>
      )}
    </g>
  );
};

// ---------- Rollladen ----------
const ShutterSVG = ({ c, opts = {} }) => {
  const { antrieb, einbau, behang } = opts;
  const slatCount = behang === "Aluminium-Behang" ? 9 : 7;
  const boxHidden = einbau === "Unterputz";
  return (
    <g>
      {!boxHidden && <rect x={90} y={18} width={220} height={34} rx="4" fill={c} />}
      {/* Antrieb */}
      <Detail k={antrieb}>
        {antrieb === "Gurtzug" && (
          <g>
            <line x1={306} y1={52} x2={330} y2={238} stroke="#c9ced4" strokeWidth="4" />
            <rect x={316} y={238} width="26" height="18" rx="4" fill="#d7dade" stroke="#9aa0a6" />
          </g>
        )}
        {antrieb === "Kurbel" && (
          <g>
            <line x1={306} y1={56} x2={332} y2={200} stroke="#9aa0a6" strokeWidth="3.5" />
            <line x1={332} y1={200} x2={332} y2={224} stroke="#9aa0a6" strokeWidth="3.5" />
            <rect x={326} y={224} width="12" height="7" rx="3" fill="#d7dade" stroke="#9aa0a6" />
          </g>
        )}
        {antrieb === "Elektrisch (Motor)" && (
          <g>
            <rect x={282} y={26} width="18" height="18" rx="4" fill="#22262a" stroke="#4a5054" />
            <circle cx={291} cy={35} r="4" fill="#e2001a" opacity="0.9" />
          </g>
        )}
      </Detail>
      {/* Behang-Lamellen */}
      {[...Array(slatCount)].map((_, i) => (
        <rect
          key={i}
          x={96}
          y={56 + i * (behang === "Aluminium-Behang" ? 12.5 : 16)}
          width={208}
          height={behang === "Aluminium-Behang" ? 8 : 11}
          rx="2"
          fill={c}
          opacity={0.92 - i * 0.03}
          stroke={behang === "Aluminium-Behang" ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.25)"}
          strokeWidth="0.6"
        />
      ))}
      <Frame x={90} y={52} w={220} h={218} c={c}>
        <rect x={104} y={66} width={192} height={190} fill="none" stroke={c} strokeWidth="6" />
      </Frame>
    </g>
  );
};

const SunSVG = ({ c, opts = {} }) => {
  const { art, antrieb } = opts;
  return (
    <g>
      <Frame x={90} y={90} w={220} h={180} c={c}>
        <rect x={104} y={104} width={192} height={152} fill="none" stroke={c} strokeWidth="6" />
      </Frame>
      {art === "Gelenkarm-Markise" ? (
        <g>
          <polygon points="80,90 320,90 352,44 48,44" fill={c} />
          {[...Array(6)].map((_, i) => (
            <line key={i} x1={62 + i * 46} y1={44} x2={94 + i * 46} y2={90} stroke="rgba(255,255,255,0.28)" strokeWidth="4" />
          ))}
        </g>
      ) : art === "Raffstore" ? (
        <g>
          {[...Array(6)].map((_, i) => (
            <rect key={i} x={96} y={96 + i * 14} width={208} height={7} rx="2" fill={c} opacity={0.9} />
          ))}
        </g>
      ) : (
        <rect x={96} y={96} width={208} height={90} fill={c} opacity="0.85" />
      )}
      {antrieb && (
        <Detail k={antrieb}>
          {antrieb === "Motor" ? (
            <g>
              <rect x={318} y={52} width="16" height="16" rx="4" fill="#22262a" stroke="#4a5054" />
              <circle cx={326} cy={60} r="3.4" fill="#e2001a" />
            </g>
          ) : (
            <g>
              <line x1={330} y1={60} x2={346} y2={170} stroke="#9aa0a6" strokeWidth="3.5" />
              <rect x={340} y={170} width="12" height="7" rx="3" fill="#d7dade" stroke="#9aa0a6" />
            </g>
          )}
        </Detail>
      )}
    </g>
  );
};

const InsectSVG = ({ c }) => (
  <Frame x={90} y={40} w={220} h={220} c={c}>
    <rect x={102} y={52} width={196} height={196} fill="url(#mesh)" stroke={c} strokeWidth="5" />
  </Frame>
);

const ConservatorySVG = ({ c }) => (
  <g>
    <polygon points="70,150 200,70 330,150" fill="url(#roofGrad)" stroke={c} strokeWidth="7" />
    <line x1={200} y1={70} x2={200} y2={150} stroke={c} strokeWidth="5" />
    <Frame x={70} y={150} w={260} h={120} c={c}>
      <line x1={135} y1={158} x2={135} y2={262} stroke={c} strokeWidth="5" />
      <line x1={265} y1={158} x2={265} y2={262} stroke={c} strokeWidth="5" />
    </Frame>
  </g>
);

const CanopySVG = ({ c }) => (
  <g>
    <rect x={150} y={90} width={100} height={180} rx="3" fill={c} />
    <rect x={162} y={102} width={76} height={156} rx="2" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="2" />
    <polygon points="90,90 310,90 290,58 110,58" fill="url(#roofGrad)" stroke={c} strokeWidth="6" />
    <line x1={118} y1={90} x2={104} y2={150} stroke={c} strokeWidth="6" strokeLinecap="round" />
    <line x1={282} y1={90} x2={296} y2={150} stroke={c} strokeWidth="6" strokeLinecap="round" />
  </g>
);

// ---------- Schiebetüren-Griffe ----------
const SliderSVG = ({ c, opts = {}, flip }) => {
  const { fluegel, griff } = opts;
  const hx = flip ? 58 : 328;
  return (
    <g>
      <Frame x={40} y={40} w={320} h={230} c={c}>
        <line x1={200} y1={48} x2={200} y2={262} stroke={c} strokeWidth="6" />
        {fluegel === "3-flügelig" && (
          <>
            <line x1={147} y1={48} x2={147} y2={262} stroke={c} strokeWidth="6" />
            <line x1={253} y1={48} x2={253} y2={262} stroke={c} strokeWidth="6" />
          </>
        )}
        <Detail k={griff}>
          {griff === "Griffmulde flächenbündig" ? (
            <ellipse cx={flip ? 62 : 332} cy={153} rx="5" ry="16" fill="#22262a" stroke="#4a5054" strokeWidth="1" />
          ) : griff === "Abschließbar" ? (
            <g>
              <rect x={hx} y={130} width="8" height="46" rx="4" fill="#d7dade" stroke="#9aa0a6" />
              <circle cx={hx + 4} cy={184} r="3.4" fill="#c4c9cf" stroke="#8b959d" strokeWidth="1" />
            </g>
          ) : (
            <rect x={hx} y={130} width="8" height="46" rx="4" fill="#d7dade" stroke="#9aa0a6" />
          )}
        </Detail>
        <g stroke="#8b959d" strokeWidth="2">
          <line x1={flip ? 210 : 150} y1={252} x2={flip ? 260 : 100} y2={252} />
          <polygon points={flip ? "262,252 252,247 252,257" : "98,252 108,247 108,257"} fill="#8b959d" />
        </g>
      </Frame>
    </g>
  );
};

const GarageSVG = ({ c, opts = {} }) => {
  const { sichtfenster, schluepftuer, griff } = opts;
  return (
    <g>
      <rect x={55} y={60} width={290} height={200} rx="4" fill={c} />
      {[...Array(5)].map((_, i) => (
        <line key={i} x1={55} y1={100 + i * 40} x2={345} y2={100 + i * 40} stroke="rgba(0,0,0,0.35)" strokeWidth="3" />
      ))}
      {sichtfenster === "Sichtfenster-Reihe" && [...Array(4)].map((_, i) => (
        <rect key={i} x={85 + i * 60} y={76} width={40} height={16} rx="3" fill="url(#glassGrad)" stroke="rgba(0,0,0,0.3)" />
      ))}
      {schluepftuer === "Schlupftür im Tor" && (
        <rect x={250} y={140} width={70} height={120} fill="none" stroke="rgba(0,0,0,0.5)" strokeWidth="3" />
      )}
      {schluepftuer === "Schlupftür seitlich" && (
        <rect x={352} y={120} width={40} height={140} rx="3" fill={c} stroke="rgba(0,0,0,0.4)" strokeWidth="2" />
      )}
      <rect x={188} y={236} width="24" height="8" rx="4" fill="#d7dade" opacity="0.8" />
      <Detail k={griff}>
        {griff === "Drucktaster" ? (
          <rect x={192} y={140} width="16" height="16" rx="4" fill="#22262a" stroke="#4a5054" />
        ) : griff === "Elektr. Schloss" ? (
          <g>
            <rect x={186} y={130} width="28" height="36" rx="5" fill="#22262a" stroke="#4a5054" />
            <circle cx={200} cy={140} r="3" fill="#e2001a" />
            {[0, 1, 2].map((i) => (
              <circle key={i} cx={194 + i * 6} cy={152} r="1.6" fill="#6b7280" />
            ))}
          </g>
        ) : (
          <g>
            <circle cx={200} cy={150} r="9" fill="#d7dade" stroke="#9aa0a6" strokeWidth="1" />
            <line x1={200} y1={150} x2={207} y2={157} stroke="#5a6066" strokeWidth="3" strokeLinecap="round" />
          </g>
        )}
      </Detail>
    </g>
  );
};

const FenceSVG = ({ c }) => (
  <g>
    {[80, 200, 320].map((x) => (
      <rect key={x} x={x - 5} y={80} width={10} height={180} rx="3" fill={c} stroke="rgba(0,0,0,0.3)" />
    ))}
    {[...Array(7)].map((_, i) => (
      <rect key={i} x={60} y={92 + i * 23} width={280} height={13} rx="2" fill={c} stroke="rgba(0,0,0,0.22)" strokeWidth="0.8" />
    ))}
  </g>
);

const PergolaSVG = ({ c }) => (
  <g>
    {[...Array(9)].map((_, i) => (
      <line key={i} x1={70 + i * 31} y1={80} x2={96 + i * 31} y2={108} stroke={c} strokeWidth="7" strokeLinecap="round" />
    ))}
    <rect x={92} y={104} width={10} height={160} rx="3" fill={c} />
    <rect x={298} y={104} width={10} height={160} rx="3" fill={c} />
    <line x1={60} y1={264} x2={340} y2={264} stroke="#4a5054" strokeWidth="4" strokeLinecap="round" />
  </g>
);

const ServiceSVG = ({ c }) => (
  <g>
    <rect x={90} y={120} width={220} height={120} rx="6" fill="none" stroke={c} strokeWidth="8" />
    <circle cx={200} cy={180} r={34} fill={c} />
    <rect x={192} y={158} width={16} height={44} rx="6" fill="#f2f2f0" transform="rotate(45 200 180)" />
    <rect x={192} y={158} width={16} height={44} rx="6" fill="#f2f2f0" transform="rotate(-45 200 180)" />
  </g>
);

const PREVIEW_FOR = {
  "Fenster": WindowSVG,
  "Haustüren": DoorSVG,
  "Rollläden": ShutterSVG,
  "Sonnenschutz": SunSVG,
  "Insektenschutz": InsectSVG,
  "Wintergarten": ConservatorySVG,
  "Vordächer": CanopySVG,
  "Schiebetüren": SliderSVG,
  "Garagentore": GarageSVG,
  "Zäune": FenceSVG,
  "Pergolen": PergolaSVG,
  "Dienstleistung / Montage": ServiceSVG,
};

export const ElementPreview = ({ productName, color, opts = {}, aspect = 1, bare = false, hint = false }) => {
  const [view, setView] = useState("aussen");
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const c = COLOR_HEX[color] || COLOR_HEX["Anthrazitgrau RAL 7016"];
  const flip = view === "innen";
  const Comp = PREVIEW_FOR[productName] || WindowSVG;
  const sx = Math.max(0.62, Math.min(1.35, Math.sqrt(aspect || 1)));

  if (bare) {
    return (
      <svg viewBox="0 0 400 300" className="w-full h-full">
        <GlassDefs />
        <g transform={`translate(${200 - 200 * sx} 0) scale(${sx} 1)`}>
          <Comp c={c} opts={opts} flip={false} />
        </g>
      </svg>
    );
  }

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    setTilt({ x: -py * 10, y: px * 16 });
  };

  return (
    <div
      data-testid="element-preview"
      onPointerMove={onMove}
      onPointerLeave={() => setTilt({ x: 0, y: 0 })}
      className="relative bg-gradient-to-b from-[#2c3034] to-[#1a1d20] rounded-2xl border border-white/10 overflow-hidden select-none"
      style={{ perspective: "900px" }}
    >
      <div className="absolute top-4 right-4 z-10 flex gap-1.5 bg-black/30 backdrop-blur-md rounded-full p-1 border border-white/10">
        {[["aussen", "Außen"], ["innen", "Innen"]].map(([k, label]) => (
          <button
            key={k}
            type="button"
            data-testid={`view-${k}`}
            onClick={() => setView(k)}
            className={`px-3.5 py-1.5 rounded-full font-display text-xs font-medium transition-colors ${
              view === k ? "bg-[#e2001a] text-white" : "text-white/55 hover:text-white"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="absolute top-4 left-4 z-10 flex items-center gap-1.5 text-white/35 text-[11px] font-display uppercase tracking-widest">
        <RotateCw size={11} /> Zum Kippen bewegen
      </div>

      <motion.div
        animate={{ rotateX: tilt.x, rotateY: tilt.y }}
        transition={{ type: "spring", stiffness: 120, damping: 14 }}
        style={{ transformStyle: "preserve-3d" }}
        className="py-10 px-6"
      >
        <svg viewBox="0 0 400 300" className="w-full max-h-[340px] drop-shadow-[0_24px_30px_rgba(0,0,0,0.45)]" style={{ transform: flip ? "scaleX(-1)" : "none" }}>
          <GlassDefs />
          <g transform={`translate(${200 - 200 * sx} 0) scale(${sx} 1)`}>
            <Comp c={c} opts={opts} flip={flip} />
          </g>
        </svg>
      </motion.div>

      <AnimatePresence>
        {hint && (
          <motion.p
            key="hint"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            data-testid="preview-live-hint"
            className="absolute bottom-10 left-1/2 -translate-x-1/2 flex items-center gap-1.5 bg-[#e2001a] text-white text-[11px] font-display font-medium px-3.5 py-1.5 rounded-full shadow-lg whitespace-nowrap"
          >
            <Sparkles size={12} /> Vorschau aktualisiert
          </motion.p>
        )}
      </AnimatePresence>

      <p className="text-center pb-4 text-white/40 text-xs font-display tracking-widest uppercase">
        {productName} · {view === "aussen" ? "Außenansicht" : "Innenansicht"}
      </p>
    </div>
  );
};
